import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Hash } from 'lucide-react';

export function ProcessOrderDialog({
  open,
  onOpenChange,
  order,
  orderSerials,
  productTrackInfo,
  availableSerials,
  processSerialAllocations,
  setProcessSerialAllocations,
  processLoading,
  onConfirmProcess,
}) {
  if (!order) return null;

  const trackedItems = (order.items || []).filter(item => productTrackInfo[item.product_id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Hash className="h-5 w-5 text-blue-400" />
            Process Order #{order.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-zinc-400 text-sm">Assign serial numbers to tracked items before processing this order.</p>

          {trackedItems.map((item, idx) => {
            const avail = availableSerials[item.product_id] || [];
            const alreadyAllocated = orderSerials.filter(s => s.product_id === item.product_id).length;
            const remaining = item.quantity - alreadyAllocated;

            if (remaining <= 0) return (
              <div key={idx} className="flex items-center gap-2 text-xs text-[#78BE20] p-2 bg-[#78BE20]/10 rounded" data-testid={`process-serial-done-${idx}`}>
                <Hash className="h-3 w-3" />
                {item.product_name}: All {item.quantity} serial(s) allocated
              </div>
            );

            return (
              <div key={idx} className="bg-zinc-800 p-3 rounded" data-testid={`process-serial-item-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white text-sm font-medium">{item.product_name}</p>
                  <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">{remaining} serial(s) needed</Badge>
                </div>
                {avail.length > 0 && (
                  <div className="mb-2">
                    <p className="text-zinc-500 text-xs mb-1">Click to select from available stock:</p>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {avail.map((s, si) => {
                        const currentLines = (processSerialAllocations[item.product_id] || '').split('\n').filter(Boolean);
                        const isSelected = currentLines.includes(s.serial_number);
                        return (
                          <button
                            key={si}
                            className={`text-xs px-2 py-0.5 rounded font-mono transition-colors ${
                              isSelected ? 'bg-[#78BE20]/20 text-[#78BE20] border border-[#78BE20]/40' : 'bg-zinc-900 hover:bg-zinc-700 text-zinc-300'
                            }`}
                            onClick={() => {
                              if (isSelected) {
                                setProcessSerialAllocations({ ...processSerialAllocations, [item.product_id]: currentLines.filter(l => l !== s.serial_number).join('\n') });
                              } else {
                                setProcessSerialAllocations({ ...processSerialAllocations, [item.product_id]: [...currentLines, s.serial_number].join('\n') });
                              }
                            }}
                            data-testid={`process-pick-serial-${idx}-${si}`}
                          >
                            {s.serial_number}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {avail.length === 0 && (
                  <p className="text-zinc-600 text-xs mb-2">No serialized stock available. Enter manually:</p>
                )}
                <Textarea
                  value={processSerialAllocations[item.product_id] || ''}
                  onChange={(e) => setProcessSerialAllocations({ ...processSerialAllocations, [item.product_id]: e.target.value })}
                  placeholder="Enter serial numbers (one per line)"
                  className="bg-zinc-900 border-zinc-700 font-mono text-xs"
                  rows={Math.min(remaining, 3)}
                  data-testid={`process-serial-input-${idx}`}
                />
                <p className="text-zinc-600 text-xs mt-1">
                  {(processSerialAllocations[item.product_id] || '').split('\n').filter(Boolean).length} of {remaining} entered
                </p>
              </div>
            );
          })}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={processLoading}
              onClick={onConfirmProcess}
              data-testid="confirm-process-btn"
            >
              <Hash className="h-4 w-4 mr-2" />
              {processLoading ? 'Processing...' : 'Allocate & Process Order'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
